import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import {
  Row,
  Col,
  Card,
  CardBody,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from 'reactstrap';

import Loading from '../../Loading';
import AccessDenied from '../../AccessDenied';
import SettingsSidebar from '../../Settings/SettingsSidebar';
import SubjectForm from '../../Settings/SubjectForm';
import {
  flattenFields,
  validateChanges,
  formatApiError,
  describeSaveResult,
  isApprovalHandled,
  isNotFound,
  isForbidden,
} from '../../Settings/fieldUtils';
import {
  getMenu,
  getSubject,
  saveSubject,
} from '../../../services/serviceSettings.service';
import { showError, showInfo } from '../../../actions/notificationActions';

import '../../../assets/css/slider.css';

// Service-scoped settings, behind the same Section/Subject/Group facade as the
// global settings page — so the sidebar and field renderer are shared verbatim.
//
// The selection lives in the query string rather than the path, so the tab route
// stays `/services/:id/settings` (the tab highlight matches on pathname) while a
// subject is still deep-linkable.
const readSelection = (search) => {
  const params = new URLSearchParams(search || '');
  const section = params.get('section');
  const subject = params.get('subject');
  return section && subject ? { section, subject } : null;
};

const EmptyState = ({ title, body }) => (
  <Card>
    <CardBody
      style={{
        minHeight: '40vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
      }}
    >
      <i
        className="now-ui-icons ui-2_settings-90"
        style={{ fontSize: '42px', color: '#9a9a9a', marginBottom: '18px' }}
      />
      <h5>{title}</h5>
      <p className="text-muted" style={{ maxWidth: '420px' }}>
        {body}
      </p>
    </CardBody>
  </Card>
);

class Settings extends Component {
  // Fields differ per service — Abuja has ABUJA_GATEWAY:PREPAID, Eko has
  // EKO_GATEWAY_PREPAID, telcos have no operations subject at all — so nothing
  // survives a change of service id.
  get emptyState() {
    return {
      menu: [],
      serviceMeta: null,
      // Read once from the menu response — it is not on subject responses — and
      // applied to every subject navigated to afterwards.
      permissions: {},
      accessDenied: false,
      selected: null, // { section, subject }
      subjectData: null,
      changed: [], // [{ key, value }]
      loading: false,
      loadingSubject: false,
      saving: false,
      saveError: null,
      loadError: null,
      pendingSelection: null, // awaiting unsaved-edits confirmation
      // Set when a link resolved to a subject this service does not have and the
      // menu had not arrived yet to redirect against.
      awaitingFallback: false,
    };
  }

  state = this.emptyState;

  get serviceId() {
    return this.props.match.params.id;
  }

  componentDidMount() {
    this.loadMenu();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.match.params.id !== this.serviceId) {
      this.setState(this.emptyState, () => this.loadMenu());
      return;
    }

    if (prevProps.location.search === this.props.location.search) return;

    const selection = readSelection(this.props.location.search);
    if (!selection) return;

    const { selected } = this.state;
    if (
      !selected ||
      selected.section !== selection.section ||
      selected.subject !== selection.subject
    ) {
      this.loadSubject(selection.section, selection.subject);
    }
  }

  navigateToSubject(section, subject, replace = false) {
    const { history, location } = this.props;
    const search = `?section=${encodeURIComponent(
      section
    )}&subject=${encodeURIComponent(subject)}`;
    const target = { pathname: location.pathname, search };

    if (replace) history.replace(target);
    else history.push(target);
  }

  firstSelection(menu) {
    const section = (menu || [])[0];
    const subject = section && (section.subjects || [])[0];
    if (!section || !subject) return null;
    return { section: section.key, subject: subject.key };
  }

  loadMenu() {
    this.setState({ loading: true, loadError: null });

    getMenu(this.serviceId)
      .then((res) => {
        const menu = res.data || [];
        this.setState(
          {
            loading: false,
            menu,
            serviceMeta: (res.meta && res.meta.service) || null,
            permissions: (res.meta && res.meta.permissions) || {},
          },
          () => {
            if (this.state.awaitingFallback) {
              this.fallBackToFirstSubject();
              return;
            }
            // Restore from the query string, otherwise open the first subject.
            const selection =
              readSelection(this.props.location.search) ||
              this.firstSelection(menu);
            if (!selection) return;

            this.loadSubject(selection.section, selection.subject);
            this.navigateToSubject(selection.section, selection.subject, true);
          }
        );
      })
      .catch((err) => {
        // The tab is hidden for roles without service-settings read access, so
        // this means a stale cached ACL or a deep link. Show the state quietly.
        if (isForbidden(err)) {
          this.setState({ loading: false, accessDenied: true });
          return;
        }
        this.setState({
          loading: false,
          loadError: formatApiError(err, 'Could not load these settings.'),
        });
        this.props.dispatch(showError(err));
      });
  }

  // Subjects are pruned when they have no fields, so a shared link can point at
  // one this service does not have. Land on the first section rather than error.
  fallBackToFirstSubject() {
    const selection = this.firstSelection(this.state.menu);

    this.setState({ awaitingFallback: false });
    if (!selection) {
      this.setState({ selected: null, subjectData: null });
      return;
    }
    this.loadSubject(selection.section, selection.subject);
    this.navigateToSubject(selection.section, selection.subject, true);
  }

  loadSubject(section, subject) {
    this.setState({
      selected: { section, subject },
      loadingSubject: true,
      changed: [],
      saveError: null,
      subjectData: null,
    });

    getSubject(this.serviceId, section, subject)
      .then((res) => {
        this.setState({ loadingSubject: false, subjectData: res.data });
      })
      .catch((err) => {
        this.setState({ loadingSubject: false });

        if (isForbidden(err)) {
          this.setState({ accessDenied: true });
          return;
        }

        if (isNotFound(err)) {
          this.props.dispatch(
            showError(
              'This service has no such settings subject — showing the first one instead.'
            )
          );
          // The menu may still be in flight; loadMenu picks this up when it lands.
          if (this.state.menu.length) this.fallBackToFirstSubject();
          else this.setState({ awaitingFallback: true });
          return;
        }

        this.props.dispatch(showError(formatApiError(err)));
      });
  }

  handleSelect = (section, subject) => {
    const { selected, changed } = this.state;
    if (
      selected &&
      selected.section === section &&
      selected.subject === subject
    ) {
      return;
    }
    if (changed.length > 0) {
      // Defer the switch until the user confirms losing their unsaved edits.
      this.setState({ pendingSelection: { section, subject } });
      return;
    }
    this.navigateToSubject(section, subject);
  };

  confirmSwitch = () => {
    const { pendingSelection } = this.state;
    // Drop edits up front so the query-driven reload doesn't see them as unsaved.
    this.setState({ pendingSelection: null, changed: [] });
    if (pendingSelection) {
      this.navigateToSubject(
        pendingSelection.section,
        pendingSelection.subject
      );
    }
  };

  cancelSwitch = () => this.setState({ pendingSelection: null });

  handleFieldChange = (key, value) => {
    this.setState((state) => ({
      changed: state.changed
        .filter((c) => c.key !== key)
        .concat({ key, value }),
    }));
  };

  handleReset = () => this.setState({ changed: [], saveError: null });

  handleSave = () => {
    const { selected, changed, subjectData, permissions } = this.state;

    // The form hides its buttons in read-only mode; this is the backstop that
    // guarantees no write leaves the page.
    if (permissions.canUpdate === false) return;

    const error = validateChanges(flattenFields(subjectData), changed);
    if (error) {
      this.setState({ saveError: error });
      this.props.dispatch(showError(error));
      return;
    }

    const submitted = changed;
    this.setState({ saving: true, saveError: null });

    saveSubject(this.serviceId, selected.section, selected.subject, submitted)
      .then((res) => {
        // meta.updated can be shorter than what we sent — values already equal to
        // what is stored are skipped rather than rewritten. That is success.
        this.props.dispatch(
          showInfo(describeSaveResult(res.message, submitted, res.meta))
        );
        // The body is the re-read subject, so re-render from it rather than
        // patching local state optimistically.
        this.setState({ saving: false, changed: [], subjectData: res.data });
      })
      .catch((err) => {
        // Roles are re-resolved from the database on every request, so update
        // access can disappear under a page that has been open a while. Flip to
        // read-only rather than letting them keep trying.
        if (isForbidden(err)) {
          this.setState((state) => ({
            saving: false,
            permissions: { ...state.permissions, canUpdate: false },
            saveError: formatApiError(
              err,
              'You no longer have permission to change these settings.'
            ),
          }));
          return;
        }

        // A 400 rejects the whole batch — nothing was written — so keep the form
        // dirty and surface the message, which names the offending key.
        this.setState({
          saving: false,
          saveError: isApprovalHandled(err)
            ? null
            : formatApiError(err, 'Could not save these settings.'),
        });
        this.props.dispatch(showError(err));
      });
  };

  renderContent() {
    const {
      menu,
      selected,
      subjectData,
      changed,
      loadingSubject,
      saving,
      saveError,
      permissions,
    } = this.state;

    if (loadingSubject) {
      return (
        <div
          className="justify-content-center text-center"
          style={{ marginTop: 60 }}
        >
          <Loading />
        </div>
      );
    }

    if (!selected || !subjectData) {
      return (
        <EmptyState
          title="No subject selected"
          body={
            menu.length
              ? 'Pick a subject on the left to view and edit its settings.'
              : 'There is nothing to show for this service.'
          }
        />
      );
    }

    return (
      <SubjectForm
        subjectData={subjectData}
        changed={changed}
        saving={saving}
        saveError={saveError}
        canUpdate={permissions.canUpdate}
        onFieldChange={this.handleFieldChange}
        onSave={this.handleSave}
        onReset={this.handleReset}
      />
    );
  }

  render() {
    const {
      menu,
      selected,
      loading,
      loadError,
      accessDenied,
      pendingSelection,
      serviceMeta,
    } = this.state;

    if (accessDenied) {
      return (
        <AccessDenied
          title="You do not have access to this service's settings"
          body="Your role does not include permission to view service settings. Ask an administrator if you need it."
        />
      );
    }

    if (loading) {
      return (
        <div
          className="justify-content-center text-center"
          style={{ marginTop: 60 }}
        >
          <Loading />
        </div>
      );
    }

    if (loadError) {
      return <EmptyState title="Settings unavailable" body={loadError} />;
    }

    if (!menu.length) {
      return (
        <EmptyState
          title="No settings for this service"
          body="This service has no configurable settings yet. Availability is controlled by the switch in the page header."
        />
      );
    }

    return (
      <div>
        {serviceMeta && (
          <p className="text-muted" style={{ marginBottom: '10px' }}>
            Settings for <strong>{serviceMeta.code}</strong> —{' '}
            {serviceMeta.name}
          </p>
        )}
        <Row>
          <Col md="4" lg="3">
            <SettingsSidebar
              menu={menu}
              selected={selected}
              onSelect={this.handleSelect}
            />
          </Col>
          <Col md="8" lg="9">
            {this.renderContent()}
          </Col>
        </Row>

        <Modal
          isOpen={!!pendingSelection}
          toggle={this.cancelSwitch}
          backdrop="static"
        >
          <ModalHeader toggle={this.cancelSwitch}>Unsaved changes</ModalHeader>
          <ModalBody>
            You have unsaved changes that will be lost. Switch subjects anyway?
          </ModalBody>
          <ModalFooter>
            <Button color="default" outline onClick={this.cancelSwitch}>
              Cancel
            </Button>
            <Button color="primary" onClick={this.confirmSwitch}>
              Discard &amp; switch
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    );
  }
}

export default withRouter(connect()(Settings));
